import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Play, Pause, SunHorizon, ArrowsClockwise, CircleNotch } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const MorningBriefing = () => {
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/briefing/today").then((r) => { if (r.data.exists) setBrief(r.data); }).catch(() => {});
    return () => { audioRef.current?.pause(); };
  }, []);

  const generate = async (force = false) => {
    setBusy(true);
    try {
      const { data } = force
        ? await api.post("/briefing/regenerate")
        : await api.get("/briefing/today?generate=1");
      setBrief(data);
      toast.success("Брифинг готов");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const togglePlay = async () => {
    if (playing) { audioRef.current?.pause(); return; }
    try {
      if (!audioRef.current) {
        const r = await fetch(`${BASE}/api/briefing/audio`, { credentials: "include" });
        if (!r.ok) throw new Error("Аудио не готово");
        const audio = new Audio(URL.createObjectURL(await r.blob()));
        audio.onplay = () => setPlaying(true);
        audio.onpause = () => setPlaying(false);
        audio.onended = () => setPlaying(false);
        audioRef.current = audio;
      }
      audioRef.current.play();
    } catch (e) { toast.error(e.message || "Не удалось воспроизвести"); }
  };

  return (
    <div className="glass-card p-5 mb-6" data-testid="morning-briefing">
      <div className="flex items-center gap-3 flex-wrap">
        <SunHorizon size={22} weight="duotone" className="text-amber-300 shrink-0" />
        <div className="flex-1 min-w-[160px]">
          <h2 className="font-heading font-semibold">Утренний брифинг</h2>
          <p className="text-xs text-muted-foreground">Погода, сон, события и план дня — голосом</p>
        </div>
        {!brief ? (
          <Button onClick={() => generate(false)} disabled={busy} data-testid="briefing-generate-btn">
            {busy ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <SunHorizon size={16} className="mr-1.5" weight="fill" />}
            Собрать брифинг
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            {brief.has_audio && (
              <Button onClick={togglePlay} data-testid="briefing-play-btn" className="rounded-full">
                {playing ? <Pause size={16} weight="fill" className="mr-1.5" /> : <Play size={16} weight="fill" className="mr-1.5" />}
                {playing ? "Пауза" : "Слушать"}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => generate(true)} disabled={busy} data-testid="briefing-regenerate-btn">
              {busy ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} />}
            </Button>
          </div>
        )}
      </div>
      {brief && (
        <p className={`text-sm text-muted-foreground mt-3 leading-relaxed cursor-pointer ${expanded ? "" : "line-clamp-2"}`}
           onClick={() => setExpanded((e) => !e)} data-testid="briefing-text">
          {brief.text}
        </p>
      )}
    </div>
  );
};
